import { AxiosError, isAxiosError } from "axios";

export type TemplateParseErrorCode =
  | "unclosed_brace"
  | "empty_var"
  | "unsupported_syntax"
  | "illegal_char";

export interface TemplateParseError {
  kind: "parse";
  message: string;
  code: TemplateParseErrorCode;
  offset: number;
  end: number;
  detail: string;
}

export interface TemplateUnknownVariable {
  path: string;
  offset: number;
  suggestion?: string;
}

export interface TemplateValidationError {
  kind: "unknown";
  message: string;
  unknown: TemplateUnknownVariable[];
}

// Catch-all для не-parse / не-unknown ошибок (network, 500, class-validator).
export interface TemplateOtherError {
  kind: "other";
  message: string;
}

export type TemplateApiError =
  | TemplateParseError
  | TemplateValidationError
  | TemplateOtherError;

const isParseError = (body: unknown): body is TemplateParseError => {
  if (!body || typeof body !== "object") return false;
  if (!("code" in body) || !("offset" in body) || !("end" in body))
    return false;
  return (
    typeof body.code === "string" &&
    typeof body.offset === "number" &&
    typeof body.end === "number"
  );
};

const isUnknownVarError = (body: unknown): body is TemplateValidationError => {
  if (!body || typeof body !== "object") return false;
  if (!("unknown" in body)) return false;
  return Array.isArray(body.unknown);
};

const fromMessageBody = (
  body: unknown,
  fallback: string,
): TemplateOtherError => {
  if (typeof body === "object" && body !== null && "message" in body) {
    const { message } = body;
    if (Array.isArray(message)) {
      // class-validator может вернуть string[] в `message` — фильтруем не-строки,
      // чтобы не вышло "[object Object]" в UI.
      const strings = message.filter(
        (item): item is string => typeof item === "string",
      );
      return { kind: "other", message: strings.join(", ") };
    }
    return { kind: "other", message: String(message) };
  }
  return { kind: "other", message: fallback };
};

// Парсит 400-ответ бэка в discriminated union: 3 формы ошибок шаблона
// (parse / unknown / other). 400 со структурой parse — невалидный синтаксис;
// 400 с массивом unknown — неизвестные переменные с suggestion'ами; всё
// остальное (5xx, network, class-validator) сворачивается в "other".
export const toTemplateApiError = (
  error: AxiosError<unknown>,
): TemplateApiError => {
  const body = error.response?.data;
  if (isParseError(body)) return { ...body, kind: "parse" };
  if (isUnknownVarError(body)) return { ...body, kind: "unknown" };
  return fromMessageBody(body, error.message);
};

// Type predicate для отлова нашей ошибки в catch'ах ниже по стеку.
export const isTemplateApiError = (e: unknown): e is TemplateApiError => {
  if (typeof e !== "object" || e === null) return false;
  if (!("kind" in e)) return false;
  return typeof e.kind === "string";
};

// Превращает любую axios-ошибку в TemplateApiError. Не-axios или не-400
// сворачиваем в "other" с тем сообщением, какое есть.
export const normalizeTemplateError = (error: unknown): TemplateApiError => {
  if (isTemplateApiError(error)) return error;
  if (isAxiosError<unknown>(error) && error.response?.status === 400) {
    return toTemplateApiError(error);
  }
  if (error instanceof Error) {
    return { kind: "other", message: error.message };
  }
  return { kind: "other", message: String(error) };
};
